/**
 * Complete I Ching Hexagram Database
 * 64 hexagrams with Shadow/Gift/Essence interpretations
 * For invisible personalization in journaling AI
 */

export const HEXAGRAMS = {
  1: {
    number: 1,
    name: "The Creative Force",
    traditional_name: "The Creative",
    shadow: "Aggression, forcing outcomes, domination, burnout from pushing too hard, controlling behavior, arrogance, destructive power",
    gift: "Natural leadership, creative initiative, inspiring action, generative power, authentic authority, pioneering spirit",
    essence: "Pure creative potential, effortless manifestation through alignment, divine will expressing through form",
    themes: ["leadership", "power", "creativity", "initiative", "authority", "ambition", "drive"],
    journal_keywords: ["lead", "create", "control", "power", "ambitious", "drive", "start", "initiate", "boss", "authority", "dominate", "force"],
    ai_guidance: {
      shadow_reframe: "When they're trying to force outcomes, help them see that their natural leadership is most powerful when it's not forcing. Ask: 'What if you led by inspiration rather than control?'",
      gift_celebration: "Acknowledge their natural ability to initiate and create. Their pioneering spirit is a gift. 'The way you naturally take charge and get things moving - that's rare and valuable.'",
      essence_invitation: "Invite them to feel into effortless creation. 'What would it feel like if this project created itself through you rather than you creating it?'",
      communication_notes: "Use bold, direct language. They respect strength and clarity. Don't be tentative."
    },
    life_area_focus: ["career", "purpose", "leadership", "creative_projects"]
  },

  2: {
    number: 2,
    name: "The Receptive Power",
    traditional_name: "The Receptive",
    shadow: "Passivity, doormat syndrome, losing self in service, victim mentality, inability to assert needs, depletion from over-giving",
    gift: "Receptive strength, nurturing leadership, collaborative power, the wisdom of yielding, supportive presence, creating space for others",
    essence: "Perfect yielding that moves mountains, the power of emptiness, receptivity as the source of all creation",
    themes: ["service", "receptivity", "support", "nurturing", "boundaries", "giving", "patience"],
    journal_keywords: ["help", "support", "give", "serve", "wait", "patient", "exhausted", "drained", "doormat", "boundary", "receive", "nurture"],
    ai_guidance: {
      shadow_reframe: "When they're depleted from over-giving, help them see that true receptivity includes receiving. 'What if your capacity to give actually depends on your willingness to receive?'",
      gift_celebration: "Honor their receptive strength. 'The way you create space for others and hold them - that's not weakness, it's profound strength.'",
      essence_invitation: "Invite them into the paradox of yielding. 'What if there's a kind of power in not-doing that's even greater than action?'",
      communication_notes: "Use gentle, supportive language. They need reassurance that their way is valid. Create safety first."
    },
    life_area_focus: ["relationships", "service", "self_care", "boundaries"]
  },

  3: {
    number: 3,
    name: "Breaking Through",
    traditional_name: "Difficulty at the Beginning",
    shadow: "Overwhelm, giving up too soon, chaos, feeling stuck, victimhood to circumstances, paralysis in the face of obstacles",
    gift: "Perseverance, breakthrough mindset, finding order in chaos, innovation through constraint, resilience",
    essence: "The sacred chaos of new beginnings, trusting the process of emergence, breakthrough as inevitable",
    themes: ["new_beginnings", "obstacles", "perseverance", "chaos", "breakthrough", "starting"],
    journal_keywords: ["overwhelmed", "stuck", "chaos", "confused", "starting", "beginning", "obstacle", "hard", "difficult", "give up", "breakthrough"],
    ai_guidance: {
      shadow_reframe: "When they're overwhelmed, normalize the messy beginning. 'Every beginning looks like chaos. This confusion is actually part of your creative process finding its form.'",
      gift_celebration: "Acknowledge their perseverance. 'The fact that you're staying with this despite the difficulty - that's real courage and strength.'",
      essence_invitation: "Invite trust in the chaos. 'What if this confusion is exactly what needs to happen right now? What's trying to emerge?'",
      communication_notes: "Be encouraging without dismissing the difficulty. They need validation that it IS hard, AND that they can do it."
    },
    life_area_focus: ["new_projects", "career_changes", "creative_blocks", "learning"]
  },

  4: {
    number: 4,
    name: "The Learner",
    traditional_name: "Youthful Folly",
    shadow: "Know-it-all attitude, refusing to learn, arrogance masking insecurity, repeating mistakes, intellectual rigidity",
    gift: "Beginner's mind, humble inquiry, enthusiastic learning, wisdom through experience, teachability",
    essence: "Infinite capacity for growth, the joy of not-knowing, wisdom that embraces mystery",
    themes: ["learning", "humility", "growth", "curiosity", "mistakes", "teaching"],
    journal_keywords: ["learn", "don't know", "mistake", "teach", "student", "confused", "understand", "curious", "arrogant", "know everything"],
    ai_guidance: {
      shadow_reframe: "When they're defensive about not knowing, celebrate the not-knowing. 'What if not having the answer yet is actually the most creative place to be?'",
      gift_celebration: "Honor their curiosity and willingness to learn. 'Your openness to learning and growing - that's how real wisdom develops.'",
      essence_invitation: "Invite them into the mystery. 'What if there's a kind of wisdom that lives in the questions themselves?'",
      communication_notes: "Be curious with them. Ask questions rather than giving answers. Model the learning process."
    },
    life_area_focus: ["learning", "personal_growth", "education", "skill_development"]
  },

  5: {
    number: 5,
    name: "Sacred Timing",
    traditional_name: "Waiting",
    shadow: "Impatience, anxiety about the future, inability to be present, rushing processes, missed opportunities from poor timing",
    gift: "Divine timing, patience as power, present moment awareness, strategic waiting, trusting the unfolding",
    essence: "Perfect timing, alignment with natural rhythms, the wisdom that everything happens when it needs to",
    themes: ["patience", "timing", "waiting", "trust", "present_moment", "anxiety"],
    journal_keywords: ["wait", "impatient", "when", "timing", "anxious", "future", "rushing", "slow", "hurry", "now", "present"],
    ai_guidance: {
      shadow_reframe: "When they're anxious about timing, help them see waiting as active. 'What if this waiting period is actually preparing you for what's coming?'",
      gift_celebration: "Acknowledge their capacity to trust timing. 'Your ability to wait for the right moment - that shows real wisdom and maturity.'",
      essence_invitation: "Invite them into divine timing. 'What if everything is happening at exactly the perfect pace, even when it doesn't feel like it?'",
      communication_notes: "Slow down your language. Use present-tense. Help them feel into the now rather than projecting into future."
    },
    life_area_focus: ["career_transitions", "relationships", "decision_making", "manifestation"]
  },

  6: {
    number: 6,
    name: "The Peacemaker",
    traditional_name: "Conflict",
    shadow: "Conflict avoidance, passive-aggressive behavior, unresolved tension, walking on eggshells, fear of confrontation",
    gift: "Healthy conflict resolution, diplomatic communication, creating peace through honesty, courageous conversation",
    essence: "Peace that includes all perspectives, harmony through authentic expression, conflict as a doorway to deeper connection",
    themes: ["conflict", "communication", "honesty", "relationships", "boundaries", "peace"],
    journal_keywords: ["conflict", "argue", "disagree", "tension", "confront", "avoid", "peace", "fight", "honest", "difficult conversation"],
    ai_guidance: {
      shadow_reframe: "When they're avoiding conflict, help them see honesty as love. 'What if saying the hard truth is actually how you create real peace?'",
      gift_celebration: "Honor their capacity for difficult conversations. 'Your willingness to address this directly - that takes real courage.'",
      essence_invitation: "Invite them into higher harmony. 'What if real peace isn't the absence of conflict but the ability to be authentic even when it's uncomfortable?'",
      communication_notes: "Be direct but compassionate. They need permission to be honest. Validate that discomfort is part of intimacy."
    },
    life_area_focus: ["relationships", "communication", "work_dynamics", "family"]
  },

  7: {
    number: 7,
    name: "Collective Leadership",
    traditional_name: "The Army",
    shadow: "Following the crowd, losing individuality, mob mentality, fear of standing out, codependency on group approval",
    gift: "Strategic collaboration, united purpose, leadership of groups, organized collective action, inspiring teams",
    essence: "Harmony of individual and collective, unity in diversity, the power of aligned group consciousness",
    themes: ["leadership", "groups", "collaboration", "individuality", "belonging", "teamwork"],
    journal_keywords: ["team", "group", "alone", "belong", "leader", "follow", "collaborate", "collective", "organize", "fit in"],
    ai_guidance: {
      shadow_reframe: "When they're losing themselves in the group, help them see individuality serves the collective. 'What if your unique perspective is exactly what the group needs?'",
      gift_celebration: "Acknowledge their leadership capacity. 'The way you bring people together while honoring each person - that's masterful.'",
      essence_invitation: "Invite them into higher unity. 'What if true belonging doesn't require you to shrink? What if the group needs ALL of you?'",
      communication_notes: "Balance individual and collective language. Help them see both/and rather than either/or."
    },
    life_area_focus: ["work", "community", "leadership", "social_life"]
  },

  8: {
    number: 8,
    name: "Authentic Contribution",
    traditional_name: "Holding Together",
    shadow: "Superficial connections, transactional relationships, using people, fear of genuine intimacy, networking without depth",
    gift: "Authentic community building, genuine contribution, meaningful connections, unity through authenticity",
    essence: "The recognition that giving and receiving are one, true contribution flows from wholeness",
    themes: ["community", "contribution", "authenticity", "relationships", "connection", "service"],
    journal_keywords: ["connect", "community", "contribute", "give", "authentic", "real", "genuine", "network", "relationship", "belong"],
    ai_guidance: {
      shadow_reframe: "When they're being transactional, invite depth. 'What would it look like to connect from who you really are rather than what you can get?'",
      gift_celebration: "Honor their authentic presence. 'The way you show up genuinely - that's what creates real community.'",
      essence_invitation: "Invite them into true contribution. 'What if your presence itself is the gift, not what you do or provide?'",
      communication_notes: "Emphasize being over doing. Help them feel their inherent value beyond utility."
    },
    life_area_focus: ["relationships", "community", "work", "service"]
  },

  9: {
    number: 9,
    name: "Focus",
    traditional_name: "The Taming Power of the Small",
    shadow: "Scattered energy, lack of focus, detail obsession, missing the big picture, perfectionism in minutiae",
    gift: "Focused attention, mastery through practice, attention to detail that serves the whole, disciplined refinement",
    essence: "The power of presence, small actions with great consciousness, perfection through focused attention",
    themes: ["focus", "discipline", "mastery", "details", "practice", "attention"],
    journal_keywords: ["focus", "distracted", "scattered", "concentrate", "detail", "practice", "master", "discipline", "perfectionist"],
    ai_guidance: {
      shadow_reframe: "When they're scattered, help them see focus as nourishing. 'What's the one thing that would make everything else easier?'",
      gift_celebration: "Acknowledge their capacity for sustained attention. 'Your ability to focus deeply on this - that's how mastery develops.'",
      essence_invitation: "Invite them into presence. 'What if being fully here with this one thing is more powerful than doing many things partially?'",
      communication_notes: "Help them prioritize. Use language of simplification and essence. One thing at a time."
    },
    life_area_focus: ["work", "creative_projects", "skill_development", "personal_practice"]
  },

  10: {
    number: 10,
    name: "Natural Being",
    traditional_name: "Treading",
    shadow: "Performing for approval, trying to be someone you're not, social anxiety, impression management, inauthenticity",
    gift: "Natural self-expression, being yourself without apology, authentic presence, confidence in your way of being",
    essence: "Effortless being, the recognition that who you are is enough, natural authenticity",
    themes: ["authenticity", "self_expression", "confidence", "being_yourself", "social_anxiety"],
    journal_keywords: ["authentic", "real", "pretend", "be myself", "anxious", "social", "performing", "act", "confidence", "enough"],
    ai_guidance: {
      shadow_reframe: "When they're performing, invite naturalness. 'What if the most interesting version of you is the one that's not trying?'",
      gift_celebration: "Celebrate their authentic expression. 'When you're just being yourself like this - that's magnetic and real.'",
      essence_invitation: "Invite them into effortless being. 'What if there's nothing you need to do to be valuable? What if you already are?'",
      communication_notes: "Normalize their natural way. Reflect back what's unique about them without judgment."
    },
    life_area_focus: ["relationships", "social_situations", "self_expression", "confidence"]
  },

  11: {
    number: 11,
    name: "Idealism",
    traditional_name: "Peace",
    shadow: "Naive idealism, denial of reality, spiritual bypassing, toxic positivity, disappointment when reality doesn't match vision",
    gift: "Visionary thinking, positive expectation, creating harmony, seeing potential, inspiring hope",
    essence: "Heaven on earth, the recognition that the ideal and real can merge, harmony as natural state",
    themes: ["idealism", "vision", "hope", "harmony", "disappointment", "expectations"],
    journal_keywords: ["ideal", "perfect", "vision", "hope", "disappointed", "expect", "harmonious", "peace", "positive", "reality"],
    ai_guidance: {
      shadow_reframe: "When they're disappointed by reality, help them hold both. 'What if you can honor the vision AND the reality as it is right now?'",
      gift_celebration: "Acknowledge their visionary capacity. 'Your ability to see what's possible - that's what creates change.'",
      essence_invitation: "Invite them into grounded idealism. 'What if the ideal is already here in seed form? What's one small way you're living it now?'",
      communication_notes: "Balance hope with realism. Help them find the sacred in the ordinary."
    },
    life_area_focus: ["purpose", "vision", "relationships", "creative_projects"]
  },

  12: {
    number: 12,
    name: "Caution",
    traditional_name: "Standstill",
    shadow: "Stagnation, giving up, resignation, playing it too safe, fear of change, stuck in comfort zone",
    gift: "Strategic pause, discernment, knowing when not to act, patience in adversity, wise restraint",
    essence: "The wisdom of stillness, understanding that sometimes doing nothing is doing everything",
    themes: ["stagnation", "pause", "discernment", "caution", "stuck", "change"],
    journal_keywords: ["stuck", "stagnant", "pause", "stop", "cautious", "wait", "safe", "comfort zone", "change", "move"],
    ai_guidance: {
      shadow_reframe: "When they're stuck, help them see stillness as strategic. 'What if this pause is gathering energy for what's next?'",
      gift_celebration: "Honor their discernment. 'Your ability to pause and assess before acting - that's wisdom, not weakness.'",
      essence_invitation: "Invite trust in stillness. 'What if this period of not-moving is exactly the movement you need right now?'",
      communication_notes: "Slow, steady language. Help them trust the pause without rushing them forward."
    },
    life_area_focus: ["career", "decision_making", "transitions", "personal_growth"]
  },

  13: {
    number: 13,
    name: "Deep Listening",
    traditional_name: "Fellowship with Men",
    shadow: "People-pleasing, superficial connections, loneliness in crowds, fear of true intimacy, clinging to tribe",
    gift: "Deep listening, authentic community, universal compassion, empathetic connection, genuine fellowship",
    essence: "Recognition of oneness, seeing yourself in others, universal love through individual connection",
    themes: ["listening", "empathy", "community", "connection", "loneliness", "friendship"],
    journal_keywords: ["listen", "hear", "understand", "lonely", "friend", "connect", "belong", "community", "empathy", "compassion"],
    ai_guidance: {
      shadow_reframe: "When they're lonely, help them go deeper not wider. 'What if one real connection matters more than many surface ones?'",
      gift_celebration: "Acknowledge their capacity for depth. 'The way you really listen and see people - that's rare and precious.'",
      essence_invitation: "Invite them into universal connection. 'What if the belonging you seek is already here in the way you connect?'",
      communication_notes: "Use empathetic, warm language. Really listen to them - model the gift."
    },
    life_area_focus: ["relationships", "friendship", "community", "emotional_life"]
  },

  14: {
    number: 14,
    name: "Prosperity Consciousness",
    traditional_name: "Possession in Great Measure",
    shadow: "Greed, hoarding, poverty consciousness, guilt about success, unworthiness around receiving abundance",
    gift: "Healthy abundance, generous prosperity, wise stewardship, capacity to receive, sharing from overflow",
    essence: "Infinite abundance, recognition that there's enough for everyone, prosperity as natural flow",
    themes: ["abundance", "money", "prosperity", "receiving", "generosity", "worth"],
    journal_keywords: ["money", "wealth", "abundant", "poor", "deserve", "receive", "give", "generous", "greedy", "enough"],
    ai_guidance: {
      shadow_reframe: "When they're in scarcity, help them see abundance. 'What if there's actually more than enough? Where do you already have abundance?'",
      gift_celebration: "Celebrate their capacity to receive. 'Your ability to allow good things in - that creates a positive flow for everyone.'",
      essence_invitation: "Invite them into infinite abundance. 'What if prosperity isn't something you earn but something you allow?'",
      communication_notes: "Use language of abundance and flow. Help them feel deserving and worthy."
    },
    life_area_focus: ["money", "career", "self_worth", "receiving"]
  },

  15: {
    number: 15,
    name: "Humility",
    traditional_name: "Modesty",
    shadow: "False humility, self-deprecation, hiding your light, diminishing yourself, martyr complex",
    gift: "Genuine humility, confident modesty, balanced self-worth, powerful restraint, grace",
    essence: "Egolessness, recognition that true power needs no display, humility as ultimate confidence",
    themes: ["humility", "modesty", "confidence", "ego", "self_worth"],
    journal_keywords: ["humble", "modest", "ego", "arrogant", "hide", "small", "diminish", "confident", "worth"],
    ai_guidance: {
      shadow_reframe: "When they're diminishing themselves, invite true humility. 'Real humility isn't making yourself small - it's being exactly who you are without ego.'",
      gift_celebration: "Honor their genuine modesty. 'The way you hold your gifts lightly - that's real grace and strength.'",
      essence_invitation: "Invite them into egoless power. 'What if true confidence needs no proof? What if you can be powerful and humble at once?'",
      communication_notes: "Balance acknowledgment with groundedness. Help them see their value without inflating."
    },
    life_area_focus: ["self_esteem", "leadership", "spirituality", "relationships"]
  },

  16: {
    number: 16,
    name: "Mastery",
    traditional_name: "Enthusiasm",
    shadow: "Perfectionism, never good enough, harsh self-criticism, skill obsession, comparing to others",
    gift: "Dedicated practice, skill development, enthusiastic learning, joyful mastery, continuous improvement",
    essence: "Effortless excellence, mastery as play, skill as self-expression, genius flowing through you",
    themes: ["mastery", "skill", "practice", "perfectionism", "excellence", "learning"],
    journal_keywords: ["master", "perfect", "practice", "skill", "learn", "improve", "expert", "good enough", "compare", "talent"],
    ai_guidance: {
      shadow_reframe: "When they're perfectionist, invite play. 'What if mastery comes through joy rather than through being hard on yourself?'",
      gift_celebration: "Acknowledge their dedication. 'Your commitment to getting better - that enthusiasm for growth is beautiful.'",
      essence_invitation: "Invite them into effortless skill. 'What if there's a place where practice becomes play? Where skill flows through you?'",
      communication_notes: "Ease the pressure. Help them find joy in the process rather than destination."
    },
    life_area_focus: ["skills", "career", "creative_practice", "personal_development"]
  },

  17: {
    number: 17,
    name: "The Eye",
    traditional_name: "Following",
    shadow: "Judgmental opinions, critical perspective, seeing only flaws, cynicism, mental rigidity",
    gift: "Clear perception, discerning vision, helpful feedback, seeing potential, wise perspective",
    essence: "Pure seeing without judgment, recognition of perfection in all things, divine perspective",
    themes: ["perception", "judgment", "opinion", "perspective", "criticism", "clarity"],
    journal_keywords: ["see", "look", "judge", "critical", "opinion", "perspective", "view", "notice", "observe", "cynical"],
    ai_guidance: {
      shadow_reframe: "When they're being critical, invite curiosity. 'What if you looked at this with interest rather than judgment? What else is true?'",
      gift_celebration: "Honor their clear seeing. 'Your ability to perceive clearly and give honest feedback - that's valuable and rare.'",
      essence_invitation: "Invite them into pure seeing. 'What if you could see this situation exactly as it is, without needing to judge it as good or bad?'",
      communication_notes: "Help them soften judgment while keeping discernment. Both/and rather than either/or."
    },
    life_area_focus: ["relationships", "work", "personal_growth", "creative_work"]
  },

  18: {
    number: 18,
    name: "Healing",
    traditional_name: "Work on What Has Been Spoiled",
    shadow: "Corruption, decay, avoiding necessary healing, repeating family patterns, toxic systems",
    gift: "Healing work, breaking generational patterns, restoration, making things whole, therapeutic presence",
    essence: "Perfect wholeness, recognition that nothing was ever truly broken, healing as remembering",
    themes: ["healing", "therapy", "patterns", "family", "restoration", "wholeness"],
    journal_keywords: ["heal", "therapy", "broken", "fix", "pattern", "family", "wound", "restore", "whole", "damage"],
    ai_guidance: {
      shadow_reframe: "When they're stuck in brokenness, invite wholeness. 'What if the healing you seek is recognition of what's already whole?'",
      gift_celebration: "Honor their healing work. 'Your willingness to do this deep work - that's how patterns break and new possibilities emerge.'",
      essence_invitation: "Invite them into inherent wholeness. 'What if nothing about you needs fixing? What if this is about remembering, not repairing?'",
      communication_notes: "Hold space for pain without pathologizing. Reflect back their wholeness even in brokenness."
    },
    life_area_focus: ["therapy", "healing", "family", "personal_growth"]
  },

  19: {
    number: 19,
    name: "Sensitivity",
    traditional_name: "Approach",
    shadow: "Oversensitivity, taking things personally, emotional reactivity, feeling everything too much, victim of emotions",
    gift: "Deep sensitivity, emotional intelligence, empathetic attunement, feeling as superpower, emotional wisdom",
    essence: "Pure feeling, recognition that sensitivity is a gift, oneness with all emotion",
    themes: ["sensitivity", "emotions", "empathy", "feeling", "reactivity"],
    journal_keywords: ["sensitive", "feel", "emotional", "cry", "hurt", "overwhelmed", "empathy", "emotion", "too much", "reactive"],
    ai_guidance: {
      shadow_reframe: "When they're overwhelmed by feelings, honor the gift. 'What if your sensitivity is actually a superpower that just needs good boundaries?'",
      gift_celebration: "Celebrate their emotional depth. 'Your capacity to feel deeply - that's what makes you able to connect and understand in ways others can't.'",
      essence_invitation: "Invite them into emotional mastery. 'What if you could feel everything fully AND stay centered? What if feeling doesn't mean drowning?'",
      communication_notes: "Validate emotions first. Never dismiss feelings. Help them see sensitivity as strength."
    },
    life_area_focus: ["emotions", "relationships", "empathy", "self_care"]
  },

  20: {
    number: 20,
    name: "Self-Awareness",
    traditional_name: "Contemplation",
    shadow: "Self-absorption, narcissism, endless navel-gazing, paralysis by analysis, disconnection from world",
    gift: "Healthy self-reflection, contemplative wisdom, knowing yourself deeply, metacognition, mindfulness",
    essence: "The witness, pure awareness, seeing the self from beyond the self",
    themes: ["self_awareness", "reflection", "meditation", "contemplation", "mindfulness"],
    journal_keywords: ["reflect", "aware", "notice", "observe", "contemplate", "meditate", "think", "analyze", "self", "conscious"],
    ai_guidance: {
      shadow_reframe: "When they're self-absorbed, invite connection. 'What do you notice about the world around you? How does this reflection serve others?'",
      gift_celebration: "Honor their self-awareness. 'Your capacity for self-reflection - that's how real growth and wisdom develop.'",
      essence_invitation: "Invite them into pure awareness. 'What if you could observe yourself without judgment? Who's the one watching?'",
      communication_notes: "Help balance inner and outer focus. Reflection that leads to action, not just more thinking."
    },
    life_area_focus: ["personal_growth", "meditation", "self_understanding", "spirituality"]
  },

  21: {
    number: 21,
    name: "Authority",
    traditional_name: "Biting Through",
    shadow: "Authoritarianism, need to control, rigid rules, fear of losing control, domination",
    gift: "Healthy authority, clear boundaries, decisive action, cutting through confusion, benevolent power",
    essence: "Natural authority, power that serves life, leadership as service",
    themes: ["authority", "control", "boundaries", "power", "leadership", "decisions"],
    journal_keywords: ["control", "authority", "power", "decide", "boundary", "rule", "lead", "boss", "command", "rigid"],
    ai_guidance: {
      shadow_reframe: "When they're controlling, invite trust. 'What if real authority comes from trusting others, not controlling them?'",
      gift_celebration: "Honor their decisiveness. 'Your ability to make clear decisions and hold boundaries - that's true leadership.'",
      essence_invitation: "Invite them into natural authority. 'What if your power is most effective when it's in service to something larger than control?'",
      communication_notes: "Respect their need for structure while inviting flexibility. Help them see authority as service."
    },
    life_area_focus: ["leadership", "work", "boundaries", "parenting"]
  },

  22: {
    number: 22,
    name: "Grace",
    traditional_name: "Grace",
    shadow: "Vanity, superficiality, beauty obsession, image management, substance without style",
    gift: "Natural grace, aesthetic intelligence, beauty consciousness, elegant expression, style with substance",
    essence: "Divine beauty, recognition that all is beautiful, form as expression of essence",
    themes: ["beauty", "grace", "aesthetics", "style", "appearance", "elegance"],
    journal_keywords: ["beautiful", "grace", "style", "appearance", "elegant", "aesthetic", "look", "image", "superficial", "vain"],
    ai_guidance: {
      shadow_reframe: "When they're focused on surface, invite depth. 'What if true beauty comes from the inside showing through, not from perfecting the outside?'",
      gift_celebration: "Celebrate their aesthetic sense. 'Your appreciation for beauty and grace - that's a gift that elevates everything you touch.'",
      essence_invitation: "Invite them into divine beauty. 'What if everything is already beautiful, including the imperfect parts?'",
      communication_notes: "Honor form AND essence. Help them see beauty in authenticity, not just perfection."
    },
    life_area_focus: ["self_expression", "creativity", "appearance", "lifestyle"]
  },

  23: {
    number: 23,
    name: "Simplicity",
    traditional_name: "Splitting Apart",
    shadow: "Over-complication, mental complexity, explaining everything, linguistic confusion, obscuring simple truths",
    gift: "Clarity of expression, simplifying complexity, elegant communication, getting to essence, teaching gift",
    essence: "Ultimate simplicity, the one truth beneath all complexity, clarity as revelation",
    themes: ["communication", "simplicity", "clarity", "teaching", "expression", "complexity"],
    journal_keywords: ["explain", "simple", "complex", "clear", "communicate", "understand", "teach", "confusing", "complicate"],
    ai_guidance: {
      shadow_reframe: "When they're overcomplicating, invite simplicity. 'What's the simplest way to say this? What's the essence?'",
      gift_celebration: "Honor their clarity. 'Your ability to take something complex and make it clear - that's a rare teaching gift.'",
      essence_invitation: "Invite them into ultimate simplicity. 'What if the truth is so simple it's almost too obvious to see?'",
      communication_notes: "Use clear, simple language. Model the gift. Help them trust that simple is powerful."
    },
    life_area_focus: ["communication", "teaching", "writing", "work"]
  },

  24: {
    number: 24,
    name: "Returning",
    traditional_name: "Return",
    shadow: "Addiction to busyness, fear of stillness, mental restlessness, inability to rest, burnout cycle",
    gift: "Renewal through rest, natural rhythms, cyclical wisdom, knowing when to pause, regeneration",
    essence: "The eternal return, recognizing that rest and action are one cycle, stillness as the source",
    themes: ["rest", "renewal", "cycles", "rhythm", "stillness", "recovery"],
    journal_keywords: ["rest", "tired", "burnout", "recover", "renew", "cycle", "rhythm", "pause", "stillness", "busy"],
    ai_guidance: {
      shadow_reframe: "When they're burned out from constant doing, invite rest as productive. 'What if resting is how you actually get more done?'",
      gift_celebration: "Honor their understanding of cycles. 'Your ability to recognize when you need to pause and renew - that's wisdom.'",
      essence_invitation: "Invite them into natural rhythm. 'What if rest and action are both essential parts of the same creative cycle?'",
      communication_notes: "Give permission to rest. Use language of cycles and seasons. Normalize the need for renewal."
    },
    life_area_focus: ["self_care", "health", "work_life_balance", "energy_management"]
  },

  25: {
    number: 25,
    name: "Innocence",
    traditional_name: "Innocence",
    shadow: "Naivety, recklessness, spiritual bypassing, foolishness, lack of discernment, childish behavior",
    gift: "Spontaneity, authenticity, beginner's mind, playfulness, trusting your instincts, natural wisdom",
    essence: "Divine spontaneity, perfect trust in life's unfolding, innocence that includes wisdom",
    themes: ["spontaneity", "trust", "playfulness", "authenticity", "instinct", "innocence"],
    journal_keywords: ["spontaneous", "trust", "play", "authentic", "instinct", "naive", "fool", "natural", "innocent", "childlike"],
    ai_guidance: {
      shadow_reframe: "When they're being reckless, invite wise innocence. 'What if you could trust your instincts AND have good boundaries?'",
      gift_celebration: "Celebrate their spontaneity. 'Your natural, spontaneous way of being - that authenticity is magnetic and refreshing.'",
      essence_invitation: "Invite them into divine trust. 'What if you could be completely spontaneous and completely wise at the same time?'",
      communication_notes: "Honor their playfulness. Help them trust their natural instincts while developing discernment."
    },
    life_area_focus: ["authenticity", "relationships", "creativity", "self_expression"]
  },

  26: {
    number: 26,
    name: "Integrity",
    traditional_name: "The Taming Power of the Great",
    shadow: "Self-righteousness, pride, egotism, selling out, compromising values, losing yourself for success",
    gift: "Strong values, authentic integrity, aligned action, standing for something, moral courage",
    essence: "Living truth, recognition that being and doing are one, integrity as wholeness",
    themes: ["integrity", "values", "authenticity", "courage", "alignment", "principles"],
    journal_keywords: ["integrity", "values", "authentic", "compromise", "sell out", "stand for", "principle", "honest", "true", "align"],
    ai_guidance: {
      shadow_reframe: "When they're self-righteous, invite humility. 'What if living your values doesn't require judging others who live differently?'",
      gift_celebration: "Honor their integrity. 'Your commitment to living aligned with your values - that's real courage and strength.'",
      essence_invitation: "Invite them into lived truth. 'What if integrity isn't about being perfect but about being whole?'",
      communication_notes: "Respect their values while inviting compassion. Help them hold firm boundaries with soft hearts."
    },
    life_area_focus: ["career", "relationships", "life_purpose", "decision_making"]
  },

  27: {
    number: 27,
    name: "Nourishment",
    traditional_name: "The Corners of the Mouth",
    shadow: "Poor boundaries around consumption, addiction, using food/substances to cope, depleting yourself to feed others",
    gift: "Healthy nourishment, knowing what feeds you, good boundaries, caring for yourself to serve others",
    essence: "Universal nourishment, recognizing you are both fed and feeding, nourishment as love",
    themes: ["nourishment", "food", "boundaries", "self_care", "giving", "receiving"],
    journal_keywords: ["eat", "nourish", "feed", "food", "care", "consume", "addiction", "boundary", "give", "receive"],
    ai_guidance: {
      shadow_reframe: "When they're depleting themselves, invite self-nourishment. 'What if taking care of yourself first is how you actually have more to give?'",
      gift_celebration: "Honor their caring nature. 'Your instinct to nourish and care for others - that comes from a beautiful, generous heart.'",
      essence_invitation: "Invite them into reciprocal nourishment. 'What if nourishing yourself and nourishing others are the same act?'",
      communication_notes: "Help them see self-care as essential, not selfish. Validate their giving while encouraging receiving."
    },
    life_area_focus: ["self_care", "health", "relationships", "boundaries"]
  },

  28: {
    number: 28,
    name: "Risk",
    traditional_name: "Preponderance of the Great",
    shadow: "Recklessness, gambling everything, fear of taking any risk, playing too safe, analysis paralysis",
    gift: "Calculated risk, courageous action, knowing when to leap, adventurous spirit, trusting yourself",
    essence: "Faith in the unknown, recognizing that all life is risk, courage as trust",
    themes: ["risk", "courage", "adventure", "fear", "leap", "trust"],
    journal_keywords: ["risk", "scared", "courage", "brave", "leap", "safe", "afraid", "adventure", "gamble", "trust"],
    ai_guidance: {
      shadow_reframe: "When they're paralyzed by fear, invite wise risk. 'What's the smallest risk you could take that would feel like courage?'",
      gift_celebration: "Celebrate their bravery. 'Your willingness to take this risk - that takes real courage and faith in yourself.'",
      essence_invitation: "Invite them into trust. 'What if the risk isn't whether you'll succeed but whether you'll trust yourself to handle whatever comes?'",
      communication_notes: "Acknowledge the fear while encouraging the courage. Help them see small steps as valid."
    },
    life_area_focus: ["career_changes", "relationships", "creativity", "life_transitions"]
  },

  29: {
    number: 29,
    name: "Commitment",
    traditional_name: "The Abysmal",
    shadow: "Addiction, escapism, drowning in emotions, dangerous patterns, fear of commitment, shallow living",
    gift: "Deep commitment, emotional depth, following through despite fear, showing up fully, devotion",
    essence: "Fearless descent into the unknown, transformation through darkness, commitment as love",
    themes: ["commitment", "depth", "emotions", "addiction", "fear", "devotion"],
    journal_keywords: ["commit", "deep", "fear", "addiction", "escape", "shallow", "depth", "devoted", "follow through", "dark"],
    ai_guidance: {
      shadow_reframe: "When they're avoiding depth, invite courage. 'What if the depth you fear is actually where your power lives?'",
      gift_celebration: "Honor their commitment. 'Your willingness to show up and stay committed even when it's hard - that's where real transformation happens.'",
      essence_invitation: "Invite them into fearless depth. 'What if you could dive into the deep waters and trust that you know how to swim?'",
      communication_notes: "Normalize the fear of depth. Help them see commitment as an act of courage, not weakness."
    },
    life_area_focus: ["relationships", "career", "personal_growth", "recovery"]
  },

  30: {
    number: 30,
    name: "Desire",
    traditional_name: "The Clinging",
    shadow: "Attachment, clinging to outcomes, neediness, obsessive desire, burning out from intensity",
    gift: "Healthy desire, passionate engagement, lightness of being, emotional intensity as fuel, radiance",
    essence: "Pure desire without attachment, recognizing desire as life force, burning as illumination",
    themes: ["desire", "passion", "attachment", "intensity", "longing", "wanting"],
    journal_keywords: ["want", "desire", "need", "attach", "cling", "passion", "intense", "long", "obsess", "burn"],
    ai_guidance: {
      shadow_reframe: "When they're attached to outcomes, invite freedom. 'What if you could want something fully AND be okay if it doesn't happen?'",
      gift_celebration: "Celebrate their passion. 'Your emotional intensity and desire - that's your life force, your fuel for living fully.'",
      essence_invitation: "Invite them into pure desire. 'What if you could desire without needing? Want without grasping?'",
      communication_notes: "Honor their intensity without pathologizing it. Help them channel desire into creative expression."
    },
    life_area_focus: ["relationships", "career", "creativity", "personal_goals"]
  },

  31: {
    number: 31,
    name: "Leadership",
    traditional_name: "Influence",
    shadow: "Manipulation, forcing your influence, needing to be heard, talking over others, egotistical leadership",
    gift: "Natural influence, authentic leadership, inspiring others, empowering communication, collaborative power",
    essence: "Leadership as service, recognizing that true influence comes from being, not doing",
    themes: ["leadership", "influence", "communication", "power", "service", "inspiration"],
    journal_keywords: ["lead", "influence", "inspire", "power", "manipulate", "heard", "listen", "guide", "direct", "impact"],
    ai_guidance: {
      shadow_reframe: "When they're forcing their influence, invite natural leadership. 'What if your influence is strongest when you're not trying to convince anyone?'",
      gift_celebration: "Honor their leadership capacity. 'The way you naturally inspire and influence others - that's authentic power.'",
      essence_invitation: "Invite them into leadership as service. 'What if leading is about empowering others, not proving yourself?'",
      communication_notes: "Help them see that listening IS leading. Model collaborative, non-forceful influence."
    },
    life_area_focus: ["leadership", "work", "relationships", "communication"]
  },

  32: {
    number: 32,
    name: "Continuity",
    traditional_name: "Duration",
    shadow: "Fear of change, clinging to the past, rigid consistency, inability to adapt, stuck in routine",
    gift: "Sustainable commitment, endurance, reliability, honoring what's working, building foundations",
    essence: "The eternal in the temporal, recognizing that true continuity includes change, constancy as flow",
    themes: ["commitment", "consistency", "endurance", "change", "reliability", "foundation"],
    journal_keywords: ["consistent", "continue", "endure", "sustain", "reliable", "change", "stuck", "routine", "foundation", "last"],
    ai_guidance: {
      shadow_reframe: "When they're rigid, invite flow. 'What if true consistency is about staying true to your values while adapting your methods?'",
      gift_celebration: "Honor their endurance. 'Your ability to stay committed and keep going - that's where real results come from.'",
      essence_invitation: "Invite them into dynamic continuity. 'What if the most reliable thing is your ability to adapt and flow?'",
      communication_notes: "Honor their need for stability while inviting necessary evolution. Both/and, not either/or."
    },
    life_area_focus: ["relationships", "career", "habits", "personal_practice"]
  },

  33: {
    number: 33,
    name: "Privacy",
    traditional_name: "Retreat",
    shadow: "Isolation, hiding, avoidance, hermit syndrome, fear of being seen, running away from connection",
    gift: "Healthy boundaries, strategic retreat, knowing when to withdraw, honoring your need for solitude, privacy as power",
    essence: "Solitude as fullness, recognizing that being alone and being lonely are different, retreat as return",
    themes: ["solitude", "boundaries", "retreat", "privacy", "isolation", "alone"],
    journal_keywords: ["alone", "solitude", "isolate", "hide", "withdraw", "retreat", "private", "boundary", "hermit", "avoid"],
    ai_guidance: {
      shadow_reframe: "When they're isolating from fear, invite choice. 'What's the difference between choosing solitude and hiding from connection?'",
      gift_celebration: "Honor their need for solitude. 'Your ability to be with yourself and honor your need for space - that's healthy and wise.'",
      essence_invitation: "Invite them into fullness in solitude. 'What if being alone can be as nourishing as being with others?'",
      communication_notes: "Normalize the need for alone time. Help distinguish healthy retreat from fearful avoidance."
    },
    life_area_focus: ["self_care", "boundaries", "relationships", "introversion"]
  },

  34: {
    number: 34,
    name: "Power",
    traditional_name: "The Power of the Great",
    shadow: "Misuse of power, force, aggression, fear of your own power, shrinking to make others comfortable",
    gift: "Authentic power, strength in service, empowered action, owning your impact, benevolent force",
    essence: "Pure power, recognizing that power and love are one, strength as vulnerability",
    themes: ["power", "strength", "force", "impact", "empowerment", "authority"],
    journal_keywords: ["power", "strong", "force", "aggressive", "weak", "shrink", "impact", "empower", "authority", "potent"],
    ai_guidance: {
      shadow_reframe: "When they're misusing power or shrinking, invite right use. 'What if your power is safest when it's in service to something larger than yourself?'",
      gift_celebration: "Celebrate their strength. 'Your natural power and strength - when you own it fully, that's when real change happens.'",
      essence_invitation: "Invite them into power as love. 'What if true power and true tenderness are the same thing?'",
      communication_notes: "Help them see power as neutral - it's about how it's used. Empower without inflating."
    },
    life_area_focus: ["leadership", "career", "personal_empowerment", "relationships"]
  },

  35: {
    number: 35,
    name: "Progress",
    traditional_name: "Progress",
    shadow: "Impatience with process, skipping steps, spiritual ambition, forcing growth, comparing your chapter 1 to others' chapter 20",
    gift: "Natural progression, honoring the journey, step-by-step growth, patience with unfolding, celebrating incremental wins",
    essence: "Effortless evolution, recognizing that growth is happening even when invisible, progress as spiral not line",
    themes: ["progress", "growth", "journey", "patience", "evolution", "development"],
    journal_keywords: ["progress", "grow", "evolve", "develop", "patient", "journey", "step", "impatient", "compare", "slow"],
    ai_guidance: {
      shadow_reframe: "When they're impatient, honor the process. 'What if every stage of this journey is exactly where you need to be right now?'",
      gift_celebration: "Acknowledge their growth. 'Look how far you've come - that progress, even when it feels slow, is real and valuable.'",
      essence_invitation: "Invite them into trust. 'What if growth is happening even in the moments that feel like nothing is changing?'",
      communication_notes: "Celebrate small wins. Help them see the spiral nature of growth - revisiting doesn't mean regressing."
    },
    life_area_focus: ["personal_growth", "career", "learning", "healing"]
  },

  36: {
    number: 36,
    name: "Crisis",
    traditional_name: "Darkening of the Light",
    shadow: "Victim of crisis, drowning in chaos, crisis addiction, dramatizing difficulties, learned helplessness",
    gift: "Grace under pressure, finding opportunity in crisis, resilience, calm in chaos, transformative challenges",
    essence: "The gift of crisis, recognizing that breakdown is breakthrough, crisis as catalyst",
    themes: ["crisis", "challenge", "resilience", "breakdown", "chaos", "difficulty"],
    journal_keywords: ["crisis", "chaos", "breakdown", "difficult", "challenge", "overwhelmed", "disaster", "emergency", "resilient", "cope"],
    ai_guidance: {
      shadow_reframe: "When they're drowning in crisis, invite perspective. 'What if this crisis is actually life reorganizing itself into something better?'",
      gift_celebration: "Honor their resilience. 'The way you're handling this difficult situation - that shows incredible strength and grace.'",
      essence_invitation: "Invite them into the gift of crisis. 'What if breakdowns are how breakthroughs happen? What's trying to emerge here?'",
      communication_notes: "Validate the difficulty without amplifying the drama. Help them find solid ground in chaos."
    },
    life_area_focus: ["life_transitions", "challenges", "resilience", "personal_growth"]
  },

  37: {
    number: 37,
    name: "Family",
    traditional_name: "The Family",
    shadow: "Enmeshment, family drama, codependency, losing yourself in family roles, unhealed family patterns",
    gift: "Healthy family bonds, honoring lineage, being yourself within family, creating chosen family, nurturing community",
    essence: "Universal family, recognizing all beings as family, individuation within connection",
    themes: ["family", "belonging", "lineage", "community", "home", "roots"],
    journal_keywords: ["family", "parent", "sibling", "home", "belong", "roots", "enmeshed", "codependent", "lineage", "ancestor"],
    ai_guidance: {
      shadow_reframe: "When they're enmeshed, invite individuation. 'What if you can deeply love your family AND be fully yourself?'",
      gift_celebration: "Honor their family bonds. 'The way you care for and honor your family connections - that's beautiful and important.'",
      essence_invitation: "Invite them into universal family. 'What if the whole world is your family? How does that change how you relate?'",
      communication_notes: "Help balance belonging and individuation. Family can be chosen, not just given."
    },
    life_area_focus: ["family", "relationships", "belonging", "identity"]
  },

  38: {
    number: 38,
    name: "Diversity",
    traditional_name: "Opposition",
    shadow: "Feeling like an outsider, opposition for its own sake, conflict with authority, alienation, stubborn difference",
    gift: "Honoring diversity, celebrating uniqueness, healthy differentiation, appreciating opposites, inclusive perspective",
    essence: "Unity in diversity, recognizing that opposition creates wholeness, the gift of difference",
    themes: ["diversity", "difference", "uniqueness", "opposition", "outsider", "inclusion"],
    journal_keywords: ["different", "outsider", "unique", "oppose", "diverse", "alien", "belong", "misfit", "special", "weird"],
    ai_guidance: {
      shadow_reframe: "When they're feeling alienated, celebrate difference. 'What if your differentness is exactly what the world needs from you?'",
      gift_celebration: "Honor their uniqueness. 'Your unique perspective and way of being - that's not something to fix, it's something to celebrate.'",
      essence_invitation: "Invite them into unity through difference. 'What if your uniqueness is how you contribute to the whole?'",
      communication_notes: "Normalize being different. Help them see diversity as strength, not deficit."
    },
    life_area_focus: ["identity", "belonging", "self_expression", "relationships"]
  },

  39: {
    number: 39,
    name: "Provocation",
    traditional_name: "Obstruction",
    shadow: "Playing victim, blaming obstacles, giving up when challenged, learned helplessness, complaining without action",
    gift: "Provocative wisdom, using obstacles as teachers, turning challenges into opportunities, determined action",
    essence: "No obstacles, recognizing that resistance creates strength, challenges as blessings in disguise",
    themes: ["obstacles", "challenges", "provocation", "resistance", "determination", "growth"],
    journal_keywords: ["obstacle", "block", "challenge", "stuck", "resist", "provoke", "difficult", "barrier", "problem", "determined"],
    ai_guidance: {
      shadow_reframe: "When they're stuck in victim mode, invite agency. 'What if this obstacle is actually showing you where your growth edge is?'",
      gift_celebration: "Honor their determination. 'The way you keep going despite the obstacles - that's where real character develops.'",
      essence_invitation: "Invite them to see through obstacles. 'What if the obstacle IS the way? What's this resistance teaching you?'",
      communication_notes: "Validate the difficulty while empowering action. Help them see obstacles as feedback, not failure."
    },
    life_area_focus: ["challenges", "personal_growth", "career", "resilience"]
  },

  40: {
    number: 40,
    name: "Liberation",
    traditional_name: "Deliverance",
    shadow: "Stuck in past, unable to forgive, holding grudges, staying in old stories, resisting liberation",
    gift: "Forgiveness, release, letting go, freedom from the past, liberation through acceptance",
    essence: "Perfect freedom, recognizing that you were never bound, liberation as natural state",
    themes: ["freedom", "forgiveness", "release", "liberation", "letting_go", "healing"],
    journal_keywords: ["free", "forgive", "release", "let go", "liberate", "stuck", "past", "grudge", "prison", "bound"],
    ai_guidance: {
      shadow_reframe: "When they're stuck in the past, invite release. 'What would it feel like to set this down? What becomes possible when you let go?'",
      gift_celebration: "Honor their capacity to forgive. 'Your willingness to release and let go - that takes tremendous courage and frees your energy for what's next.'",
      essence_invitation: "Invite them into natural freedom. 'What if you were never actually bound by this? What if freedom is your natural state?'",
      communication_notes: "Support forgiveness without forcing it. Let go happens in its own time. Normalize the process."
    },
    life_area_focus: ["healing", "relationships", "personal_growth", "spirituality"]
  },

  41: {
    number: 41,
    name: "Decrease",
    traditional_name: "Decrease",
    shadow: "Deprivation mentality, self-denial, martyrdom, artificial scarcity, punishing yourself",
    gift: "Healthy simplification, minimalism as choice, knowing what's essential, strategic decrease, less is more",
    essence: "Abundance through simplicity, recognizing that decrease reveals essence, emptiness as fullness",
    themes: ["simplicity", "minimalism", "essence", "decrease", "letting_go", "essentials"],
    journal_keywords: ["simple", "less", "decrease", "minimal", "essential", "reduce", "deprive", "sacrifice", "enough", "limit"],
    ai_guidance: {
      shadow_reframe: "When they're in deprivation, invite chosen simplicity. 'What if simplifying is about revealing what matters, not punishing yourself?'",
      gift_celebration: "Honor their minimalism. 'Your ability to discern what's truly essential - that clarity is powerful and freeing.'",
      essence_invitation: "Invite them into abundance through less. 'What if having less of what doesn't matter creates space for more of what does?'",
      communication_notes: "Help distinguish healthy simplicity from deprivation. Less can be more when it's a choice."
    },
    life_area_focus: ["lifestyle", "possessions", "focus", "priorities"]
  },

  42: {
    number: 42,
    name: "Increase",
    traditional_name: "Increase",
    shadow: "Greed, more-is-better mentality, expansion without foundation, overwhelm from too much, spiritual materialism",
    gift: "Healthy growth, sustainable expansion, generous increase, sharing abundance, growing with wisdom",
    essence: "Infinite increase, recognizing that abundance multiplies when shared, growth as natural expression",
    themes: ["growth", "expansion", "abundance", "increase", "generosity", "sharing"],
    journal_keywords: ["grow", "increase", "more", "expand", "abundant", "multiply", "share", "generous", "greedy", "too much"],
    ai_guidance: {
      shadow_reframe: "When they're in greed mode, invite sufficiency. 'What if you already have enough? What if more isn't the answer?'",
      gift_celebration: "Celebrate their abundance. 'Your generous spirit and willingness to share increase - that's how abundance multiplies for everyone.'",
      essence_invitation: "Invite them into infinite increase. 'What if the more you share, the more there is? What if abundance is unlimited?'",
      communication_notes: "Help them see that sustainable increase includes periods of consolidation. Growth needs roots."
    },
    life_area_focus: ["career", "finances", "personal_growth", "relationships"]
  },

  43: {
    number: 43,
    name: "Breakthrough",
    traditional_name: "Break-through",
    shadow: "Insight addiction, always seeking the next breakthrough, devaluing incremental progress, forcing breakthroughs",
    gift: "True breakthrough moments, quantum leaps, sudden clarity, revolutionary insights, transformative realizations",
    essence: "Breakthrough as natural, recognizing that evolution happens in leaps and plateaus, trust in timing",
    themes: ["breakthrough", "insight", "clarity", "revolution", "transformation", "awakening"],
    journal_keywords: ["breakthrough", "insight", "clarity", "aha", "realize", "understand", "revolutionary", "transform", "quantum", "sudden"],
    ai_guidance: {
      shadow_reframe: "When they're addicted to breakthroughs, invite process. 'What if the plateau between breakthroughs is where the integration happens?'",
      gift_celebration: "Celebrate their insight. 'This breakthrough you're having - honor it, let it land, let it reorganize everything.'",
      essence_invitation: "Invite them into trust. 'What if breakthroughs come when you stop forcing them? What if they arrive in their own perfect timing?'",
      communication_notes: "Help them value both breakthrough and integration. Honor the ah-ha while grounding it."
    },
    life_area_focus: ["personal_growth", "creativity", "spirituality", "problem_solving"]
  },

  44: {
    number: 44,
    name: "Synergy",
    traditional_name: "Coming to Meet",
    shadow: "Codependency, losing yourself in partnership, fear of intimacy, transactional relating, using people",
    gift: "True partnership, synergistic collaboration, 1+1=3, complementary strengths, sacred union",
    essence: "Perfect synergy, recognizing that union creates something neither could alone, love as multiplication",
    themes: ["partnership", "synergy", "collaboration", "relationship", "union", "teamwork"],
    journal_keywords: ["partner", "together", "collaborate", "synergy", "team", "union", "codependent", "merge", "complement", "combine"],
    ai_guidance: {
      shadow_reframe: "When they're codependent, invite wholeness first. 'What if you can be completely whole AND completely connected?'",
      gift_celebration: "Honor their collaborative spirit. 'The way you create synergy with others - that's magic, that's how extraordinary things happen.'",
      essence_invitation: "Invite them into sacred union. 'What if true partnership is two wholes choosing to create something even greater?'",
      communication_notes: "Help balance autonomy and connection. Partnership requires two individuals, not two halves."
    },
    life_area_focus: ["relationships", "collaboration", "work", "partnerships"]
  },

  45: {
    number: 45,
    name: "Gathering",
    traditional_name: "Gathering Together",
    shadow: "Fear of being alone, people-pleasing to fit in, losing identity in groups, needing constant social validation",
    gift: "Natural gathering, creating community, bringing people together, facilitating connection, group consciousness",
    essence: "Universal gathering, recognizing that separation is illusion, we are always already gathered",
    themes: ["community", "gathering", "belonging", "social", "connection", "groups"],
    journal_keywords: ["gather", "community", "together", "group", "belong", "social", "connect", "people", "alone", "crowd"],
    ai_guidance: {
      shadow_reframe: "When they need constant validation, invite inner fullness. 'What if you could feel complete whether alone or with others?'",
      gift_celebration: "Honor their gift for gathering. 'Your natural ability to bring people together and create community - that's a beautiful gift.'",
      essence_invitation: "Invite them into deep belonging. 'What if you always belong, whether in a crowd or alone? What if you are the gathering place?'",
      communication_notes: "Help them find belonging that doesn't require losing themselves. Community includes individuality."
    },
    life_area_focus: ["social_life", "community", "belonging", "relationships"]
  },

  46: {
    number: 46,
    name: "Ascending",
    traditional_name: "Pushing Upward",
    shadow: "Ambition without foundation, climbing the wrong ladder, success addiction, pushing too hard, burnout from striving",
    gift: "Organic ascent, right ambition, sustainable growth, rising naturally, success with integrity",
    essence: "Effortless rising, recognizing that true ascent is a return to what you already are",
    themes: ["ambition", "success", "growth", "rising", "achievement", "ascent"],
    journal_keywords: ["ambition", "success", "achieve", "rise", "climb", "grow", "strive", "push", "ascend", "up"],
    ai_guidance: {
      shadow_reframe: "When they're pushing too hard, invite organic growth. 'What if real success comes from being pulled by what you love, not pushed by what you lack?'",
      gift_celebration: "Celebrate their ambition. 'Your desire to grow and rise - when it comes from authentic aspiration, that's beautiful and powerful.'",
      essence_invitation: "Invite them into effortless rising. 'What if you don't have to force your way up? What if you naturally rise when you're in alignment?'",
      communication_notes: "Help them distinguish healthy ambition from compensatory striving. Success is about alignment, not just achievement."
    },
    life_area_focus: ["career", "ambition", "personal_growth", "success"]
  },

  47: {
    number: 47,
    name: "Confusion",
    traditional_name: "Oppression",
    shadow: "Mental oppression, confusion as identity, staying stuck in not-knowing, learned confusion, mental fog",
    gift: "Embracing confusion as fertile ground, comfort with not-knowing, clarity emerging from chaos, wisdom through bewilderment",
    essence: "Confusion as revelation, recognizing that not-knowing is the doorway, the wisdom of uncertainty",
    themes: ["confusion", "clarity", "uncertainty", "not_knowing", "mental_fog", "bewilderment"],
    journal_keywords: ["confused", "don't know", "uncertain", "fog", "bewildered", "unclear", "lost", "understand", "clarity", "oppressed"],
    ai_guidance: {
      shadow_reframe: "When they're stuck in confusion, invite curiosity. 'What if this confusion is actually the first stage of a new understanding trying to emerge?'",
      gift_celebration: "Honor their comfort with not-knowing. 'Your ability to sit with uncertainty without needing immediate answers - that's real wisdom.'",
      essence_invitation: "Invite them into fertile confusion. 'What if confusion is where all new understanding is born? What's gestating in this not-knowing?'",
      communication_notes: "Normalize confusion as part of the process. Don't rush to clarity - let understanding emerge naturally."
    },
    life_area_focus: ["decision_making", "transitions", "learning", "personal_growth"]
  },

  48: {
    number: 48,
    name: "The Well",
    traditional_name: "The Well",
    shadow: "Feeling depleted, empty well syndrome, giving from depletion, not refilling yourself, running on empty",
    gift: "Deep resources, knowing your well is deep, sustainable giving from fullness, replenishing yourself, inexhaustible source",
    essence: "Infinite wellspring, recognizing you are connected to infinite source, the well that never runs dry",
    themes: ["resources", "depletion", "sustainability", "source", "replenishment", "depth"],
    journal_keywords: ["depleted", "empty", "resource", "well", "refill", "replenish", "source", "deep", "exhausted", "sustainable"],
    ai_guidance: {
      shadow_reframe: "When they're depleted, invite replenishment. 'What if your well needs to be refilled before you can draw from it? What replenishes you?'",
      gift_celebration: "Honor their depth. 'You have such deep resources within you - when you stay connected to your source, you have so much to give.'",
      essence_invitation: "Invite them into infinite source. 'What if you're connected to a well that never runs dry? What if replenishing is just remembering that connection?'",
      communication_notes: "Help them distinguish giving from fullness vs. giving from depletion. Sustainability is key."
    },
    life_area_focus: ["self_care", "energy", "service", "sustainability"]
  },

  49: {
    number: 49,
    name: "Revolution",
    traditional_name: "Revolution",
    shadow: "Revolution for its own sake, constant upheaval, inability to commit, burning bridges, destructive change",
    gift: "Necessary revolution, transformative change, shedding the old, evolutionary leaps, sacred disruption",
    essence: "Perpetual revolution, recognizing that life is constant transformation, change as the only constant",
    themes: ["revolution", "change", "transformation", "upheaval", "evolution", "shedding"],
    journal_keywords: ["revolution", "change", "transform", "shed", "upheaval", "burn", "destroy", "new", "evolution", "disrupt"],
    ai_guidance: {
      shadow_reframe: "When they're creating chaos, invite purposeful change. 'What if revolution serves something? What's trying to be born through this change?'",
      gift_celebration: "Honor their courage to change. 'Your willingness to revolutionize your life when it's needed - that takes incredible courage.'",
      essence_invitation: "Invite them into constant transformation. 'What if you can change everything while staying true to your essence? What if you're the constant in the change?'",
      communication_notes: "Support necessary change while questioning destructive patterns. Help them change with purpose."
    },
    life_area_focus: ["major_transitions", "transformation", "letting_go", "new_beginnings"]
  },

  50: {
    number: 50,
    name: "Values",
    traditional_name: "The Caldron",
    shadow: "Values confusion, adopting others' values, moral ambiguity, not knowing what you stand for, wishy-washy principles",
    gift: "Clear values, knowing what matters, standing for something, moral clarity, values-driven action",
    essence: "Living truth, recognizing that your values are expressions of universal truth, integrity as wholeness",
    themes: ["values", "principles", "integrity", "morality", "standards", "ethics"],
    journal_keywords: ["values", "principle", "integrity", "moral", "stand for", "believe", "ethics", "right", "wrong", "matter"],
    ai_guidance: {
      shadow_reframe: "When they lack clarity, invite discernment. 'What really matters to you? What would you stand for even if no one agreed?'",
      gift_celebration: "Honor their clarity. 'The way you know what you value and stand for it - that's integrity and strength.'",
      essence_invitation: "Invite them into lived values. 'What if your values aren't just beliefs but the way you show up every day? How are you living them now?'",
      communication_notes: "Help them clarify what matters without imposing values. Values are discovered, not prescribed."
    },
    life_area_focus: ["identity", "decision_making", "integrity", "life_purpose"]
  },

  51: {
    number: 51,
    name: "Shock",
    traditional_name: "The Arousing",
    shadow: "Chronic anxiety, stress addiction, living in perpetual shock, panic, adrenaline junkie, overwhelm",
    gift: "Vitality, aliveness, healthy stimulation, catalytic breakthroughs, awakening shock, dynamic energy",
    essence: "The awakening thunderbolt, recognizing that shock catalyzes evolution, crisis as opportunity",
    themes: ["shock", "awakening", "vitality", "stress", "stimulation", "breakthrough"],
    journal_keywords: ["shock", "anxious", "stress", "panic", "alive", "stimulate", "breakthrough", "awaken", "crisis", "adrenaline"],
    ai_guidance: {
      shadow_reframe: "When they're chronically stressed, invite grounding. 'What if you could feel alive without living in constant stress? Where's the healthy excitement?'",
      gift_celebration: "Celebrate their vitality. 'Your aliveness and capacity to be stimulated by life - that's energy and enthusiasm that many people wish they had.'",
      essence_invitation: "Invite them into awakening. 'What if this shock is life waking you up to something important? What's the invitation here?'",
      communication_notes: "Help distinguish healthy excitement from chronic stress. Vitality without burnout."
    },
    life_area_focus: ["stress", "vitality", "breakthroughs", "awakening"]
  },

  52: {
    number: 52,
    name: "Stillness",
    traditional_name: "Keeping Still",
    shadow: "Paralysis, stagnation, stuck stillness, avoiding life, depressive inertia, frozen",
    gift: "Meditative stillness, contemplative depth, peaceful presence, knowing when to be still, restorative pause",
    essence: "Perfect stillness, recognizing that stillness contains all movement, the void as source",
    themes: ["stillness", "meditation", "peace", "contemplation", "pause", "presence"],
    journal_keywords: ["still", "stuck", "frozen", "peace", "quiet", "meditate", "pause", "contemplate", "stop", "paralyze"],
    ai_guidance: {
      shadow_reframe: "When they're paralyzed, invite chosen stillness. 'What's the difference between being stuck and choosing to pause? What if this stillness is gathering energy?'",
      gift_celebration: "Honor their stillness. 'Your capacity to be still and present - that's where real peace and clarity come from.'",
      essence_invitation: "Invite them into the void. 'What if in the deepest stillness, everything is possible? What emerges from this quietness?'",
      communication_notes: "Help distinguish depression from meditation. Stillness can be active and alive."
    },
    life_area_focus: ["meditation", "peace", "contemplation", "rest"]
  },

  53: {
    number: 53,
    name: "Gradual Progress",
    traditional_name: "Development",
    shadow: "Impatience with slow progress, wanting instant results, comparing your beginning to others' middle, frustration with gradual",
    gift: "Patience with process, honoring natural timing, step-by-step development, trusting gradual unfolding, sustainable growth",
    essence: "Natural development, recognizing that everything matures in its own time, patience as power",
    themes: ["patience", "gradual", "development", "timing", "maturation", "process"],
    journal_keywords: ["gradual", "slow", "patient", "develop", "mature", "timing", "process", "step", "impatient", "instant"],
    ai_guidance: {
      shadow_reframe: "When they're impatient, honor the gradual. 'What if slow and steady really does win? What if this gradual progress is exactly right?'",
      gift_celebration: "Celebrate their patience. 'Your ability to trust the gradual unfolding - that's maturity and wisdom.'",
      essence_invitation: "Invite them into natural timing. 'What if everything develops at exactly the right pace? What if there's no rushing what's naturally maturing?'",
      communication_notes: "Help them see incremental progress. Small steps compound over time."
    },
    life_area_focus: ["personal_growth", "career", "learning", "relationships"]
  },

  54: {
    number: 54,
    name: "Ambition",
    traditional_name: "The Marrying Maiden",
    shadow: "Climbing through others, using relationships for advancement, social climbing, sacrificing integrity for success",
    gift: "Right ambition, win-win partnerships, mutually beneficial relationships, ethical advancement, collaborative success",
    essence: "Sacred ambition, recognizing that true success uplifts everyone, rising together",
    themes: ["ambition", "partnership", "advancement", "relationships", "success", "ethics"],
    journal_keywords: ["ambitious", "advance", "climb", "partner", "use", "success", "ethical", "relationship", "benefit", "rise"],
    ai_guidance: {
      shadow_reframe: "When they're using people, invite integrity. 'What if success that comes from using others isn't real success? What's the win-win here?'",
      gift_celebration: "Honor their ambition with integrity. 'Your drive to succeed while maintaining your ethics - that's how you build something truly sustainable.'",
      essence_invitation: "Invite them into sacred ambition. 'What if your success could lift everyone around you? How do you rise together?'",
      communication_notes: "Help balance ambition with ethics. Success with soul matters more than success at any cost."
    },
    life_area_focus: ["career", "relationships", "success", "integrity"]
  },

  55: {
    number: 55,
    name: "Abundance",
    traditional_name: "Abundance",
    shadow: "Peak abundance anxiety, fear of losing it all, inability to enjoy success, waiting for the other shoe to drop",
    gift: "Enjoying abundance, peak experiences, celebrating success, gratitude, fullness, capacity to receive",
    essence: "Perpetual abundance, recognizing that fullness is natural state, nothing to hold or lose",
    themes: ["abundance", "success", "fullness", "gratitude", "enjoyment", "peak"],
    journal_keywords: ["abundant", "full", "success", "peak", "enjoy", "grateful", "plenty", "afraid", "lose", "celebrate"],
    ai_guidance: {
      shadow_reframe: "When they can't enjoy abundance, invite presence. 'What if you could fully receive this good without waiting for it to end? What if you just enjoyed it now?'",
      gift_celebration: "Celebrate with them. 'This abundance you're experiencing - you get to fully enjoy it. Your gratitude and celebration multiplies the good.'",
      essence_invitation: "Invite them into perpetual abundance. 'What if abundance is always available? What if this fullness is your natural state?'",
      communication_notes: "Give permission to enjoy. Help them celebrate without guilt or fear."
    },
    life_area_focus: ["success", "gratitude", "enjoyment", "receiving"]
  },

  56: {
    number: 56,
    name: "The Wanderer",
    traditional_name: "The Wanderer",
    shadow: "Rootlessness, running away, inability to commit, eternal seeking, homeless feeling, avoiding depth",
    gift: "Adventurous spirit, openness to experience, travel as growth, comfortable with change, finding home within",
    essence: "At home everywhere, recognizing that you are the home, belonging to yourself",
    themes: ["travel", "adventure", "seeking", "rootlessness", "wandering", "home"],
    journal_keywords: ["wander", "travel", "seek", "roam", "rootless", "home", "belong", "explore", "adventure", "restless"],
    ai_guidance: {
      shadow_reframe: "When they're running, invite grounding. 'What are you seeking out there that might already be here? What if home is within you?'",
      gift_celebration: "Honor their adventurous spirit. 'Your openness to new experiences and willingness to explore - that's how you grow and learn.'",
      essence_invitation: "Invite them into inner home. 'What if you could wander freely because you're always home in yourself? What if you belong everywhere because you belong to you?'",
      communication_notes: "Balance freedom with rootedness. Help them find home within while honoring the journey."
    },
    life_area_focus: ["travel", "exploration", "identity", "belonging"]
  },

  57: {
    number: 57,
    name: "Intuition",
    traditional_name: "The Gentle",
    shadow: "Ignoring intuition, overthinking gut feelings, second-guessing yourself, mental override of knowing",
    gift: "Strong intuition, trusting your knowing, subtle perception, gentle wisdom, following inner guidance",
    essence: "Pure knowing, recognizing intuition as direct access to truth, wisdom beyond mind",
    themes: ["intuition", "knowing", "guidance", "inner_wisdom", "trust", "subtle"],
    journal_keywords: ["intuition", "feel", "sense", "know", "gut", "trust", "ignore", "overthink", "subtle", "guide"],
    ai_guidance: {
      shadow_reframe: "When they're overthinking, invite intuition. 'What does your gut tell you? What would you do if you trusted your first knowing?'",
      gift_celebration: "Honor their intuition. 'Your intuitive knowing - that's real wisdom. When you trust it, it guides you so well.'",
      essence_invitation: "Invite them into pure knowing. 'What if your intuition is direct access to truth? What if you already know?'",
      communication_notes: "Help them distinguish intuition from fear. First knowing vs. reactive thinking."
    },
    life_area_focus: ["decision_making", "inner_guidance", "trust", "knowing"]
  },

  58: {
    number: 58,
    name: "Joy",
    traditional_name: "The Joyous",
    shadow: "Forced positivity, denying pain, superficial happiness, joy-chasing, bypassing difficulty with false cheer",
    gift: "Genuine joy, finding delight, natural cheerfulness, uplifting presence, authentic happiness",
    essence: "Pure joy, recognizing joy as natural state, happiness independent of circumstances",
    themes: ["joy", "happiness", "delight", "cheerfulness", "positivity", "pleasure"],
    journal_keywords: ["joy", "happy", "delight", "cheer", "positive", "fun", "pleasure", "fake", "forced", "enjoy"],
    ai_guidance: {
      shadow_reframe: "When they're forcing happiness, invite authenticity. 'What if real joy includes all your feelings? What if you can be sad and still fundamentally okay?'",
      gift_celebration: "Celebrate their joy. 'Your natural cheerfulness and ability to find delight - that uplifts everyone around you.'",
      essence_invitation: "Invite them into unconditional joy. 'What if joy is your natural state, underneath everything else? What if you can access it even in difficulty?'",
      communication_notes: "Honor authentic feeling. Joy doesn't require suppressing sadness - it's deeper than mood."
    },
    life_area_focus: ["happiness", "emotions", "authenticity", "well_being"]
  },

  59: {
    number: 59,
    name: "Intimacy",
    traditional_name: "Dispersion",
    shadow: "Fear of intimacy, walls up, emotional distance, never letting anyone in, protective barriers",
    gift: "Deep intimacy, vulnerability as strength, letting people in, emotional openness, authentic connection",
    essence: "Ultimate intimacy, recognizing that boundaries and openness are one, intimacy with all life",
    themes: ["intimacy", "vulnerability", "connection", "openness", "barriers", "closeness"],
    journal_keywords: ["intimate", "vulnerable", "close", "open", "wall", "barrier", "distance", "let in", "guard", "connection"],
    ai_guidance: {
      shadow_reframe: "When they have walls up, invite safe vulnerability. 'What if you could be open AND safe? What would it take to let someone see you?'",
      gift_celebration: "Honor their vulnerability. 'Your willingness to be intimate and let people in - that's real courage and creates real connection.'",
      essence_invitation: "Invite them into ultimate intimacy. 'What if you could be intimate with life itself? What if vulnerability is your greatest strength?'",
      communication_notes: "Create safety for vulnerability. Intimacy requires trust built slowly."
    },
    life_area_focus: ["relationships", "vulnerability", "connection", "trust"]
  },

  60: {
    number: 60,
    name: "Limitation",
    traditional_name: "Limitation",
    shadow: "Feeling limited, victim of circumstances, boxed in, restricted, trapped by constraints",
    gift: "Creative constraint, working within limits, boundaries as creative force, accepting what is",
    essence: "Freedom through limitation, recognizing that constraints create form, limitation as liberation",
    themes: ["limitation", "constraint", "boundaries", "restriction", "creativity", "acceptance"],
    journal_keywords: ["limit", "restrict", "constrain", "boundary", "trap", "box", "accept", "work with", "creative", "free"],
    ai_guidance: {
      shadow_reframe: "When they feel trapped, invite creativity. 'What if these limits are actually creating focus? What becomes possible within these constraints?'",
      gift_celebration: "Honor their creativity with limits. 'Your ability to work creatively within constraints - that's where true innovation happens.'",
      essence_invitation: "Invite them into freedom through form. 'What if limitations actually free you by giving you something to work with? What if form creates possibility?'",
      communication_notes: "Help reframe limits as creative containers, not prisons. Boundaries can be generative."
    },
    life_area_focus: ["creativity", "problem_solving", "acceptance", "resourcefulness"]
  },

  61: {
    number: 61,
    name: "Mystery",
    traditional_name: "Inner Truth",
    shadow: "Need to know everything, intolerance of mystery, rigid certainty, skepticism that closes wonder",
    gift: "Comfort with mystery, wonder, curiosity, openness to unknown, living the questions",
    essence: "The great mystery, recognizing that not-knowing is the most honest position, wonder as wisdom",
    themes: ["mystery", "wonder", "curiosity", "unknown", "questions", "openness"],
    journal_keywords: ["mystery", "wonder", "curious", "unknown", "question", "don't know", "open", "certain", "skeptical", "awe"],
    ai_guidance: {
      shadow_reframe: "When they need certainty, invite mystery. 'What if not having all the answers is actually the most truthful place? What if mystery is okay?'",
      gift_celebration: "Honor their wonder. 'Your capacity to live with mystery and stay curious - that's how you stay open to new understanding.'",
      essence_invitation: "Invite them into the great mystery. 'What if the biggest truths can't be known but only lived? What if wonder is wiser than certainty?'",
      communication_notes: "Normalize not-knowing. Help them find comfort in questions without needing immediate answers."
    },
    life_area_focus: ["spirituality", "learning", "curiosity", "philosophy"]
  },

  62: {
    number: 62,
    name: "Details",
    traditional_name: "Preponderance of the Small",
    shadow: "Lost in details, missing forest for trees, perfectionism in minutiae, obsessing over small things",
    gift: "Attention to detail, precision, getting the small things right, thoroughness, excellence in execution",
    essence: "Small as sacred, recognizing that god is in the details, the infinite in the infinitesimal",
    themes: ["details", "precision", "thoroughness", "small_things", "perfectionism", "care"],
    journal_keywords: ["detail", "precise", "thorough", "perfect", "small", "minutiae", "careful", "obsess", "exact", "meticulous"],
    ai_guidance: {
      shadow_reframe: "When they're lost in details, invite perspective. 'What's this detail serving? Does this precision serve the whole or just the part?'",
      gift_celebration: "Honor their thoroughness. 'Your attention to detail and care for getting things right - that's where excellence comes from.'",
      essence_invitation: "Invite them into sacred details. 'What if every small thing you do with care is actually a profound act? What if small is sacred?'",
      communication_notes: "Help balance detail with big picture. Details matter when they serve the whole."
    },
    life_area_focus: ["work", "craftsmanship", "excellence", "precision"]
  },

  63: {
    number: 63,
    name: "Doubt",
    traditional_name: "After Completion",
    shadow: "Chronic doubt, second-guessing, never satisfied, fear that it's not enough, imposter syndrome",
    gift: "Healthy skepticism, quality control, refinement, continuous improvement, discerning mind",
    essence: "Doubt as doorway, recognizing that questioning leads to deeper knowing, uncertainty as friend",
    themes: ["doubt", "questioning", "skepticism", "refinement", "improvement", "uncertainty"],
    journal_keywords: ["doubt", "question", "skeptical", "uncertain", "imposter", "enough", "second guess", "refine", "improve", "sure"],
    ai_guidance: {
      shadow_reframe: "When they're paralyzed by doubt, invite trust. 'What if some doubt is healthy but too much doubt prevents you from living? What do you know for sure?'",
      gift_celebration: "Honor their discernment. 'Your questioning mind and ability to refine - that's how you create quality and depth.'",
      essence_invitation: "Invite them into doubt as teacher. 'What if doubt is just asking you to go deeper? What becomes clear when you really question?'",
      communication_notes: "Validate healthy skepticism while addressing chronic self-doubt. Help distinguish the two."
    },
    life_area_focus: ["self_confidence", "decision_making", "quality", "discernment"]
  },

  64: {
    number: 64,
    name: "Imagination",
    traditional_name: "Before Completion",
    shadow: "Lost in fantasy, avoiding reality, living in imagination instead of life, escapism, unrealistic dreaming",
    gift: "Visionary imagination, creative visualization, dreaming new possibilities, innovative thinking, inspired vision",
    essence: "Imagination as reality creation, recognizing that everything begins in imagination, dreaming as power",
    themes: ["imagination", "vision", "creativity", "dreaming", "fantasy", "possibility"],
    journal_keywords: ["imagine", "dream", "vision", "fantasy", "creative", "visualize", "escape", "possible", "unrealistic", "ideal"],
    ai_guidance: {
      shadow_reframe: "When they're escaping into fantasy, invite grounded vision. 'What if you could dream big AND take small real steps? How does imagination serve reality?'",
      gift_celebration: "Celebrate their imagination. 'Your visionary capacity to imagine what's possible - that's how all creation begins.'",
      essence_invitation: "Invite them into creative imagination. 'What if your imagination is actually showing you what wants to be born? What if dreams are blueprints?'",
      communication_notes: "Honor their vision while encouraging action. Help bridge imagination and manifestation."
    },
    life_area_focus: ["creativity", "vision", "manifestation", "innovation"]
  }
};

// Helper function to get hexagram by number
export function getHexagram(number) {
  return HEXAGRAMS[number];
}

// Helper function to get hexagrams by theme
export function getHexagramsByTheme(theme) {
  return Object.values(HEXAGRAMS).filter(hex =>
    hex.themes.includes(theme)
  );
}

// Helper function to search hexagrams by keyword
export function searchHexagramsByKeyword(keyword) {
  const lowerKeyword = keyword.toLowerCase();
  return Object.values(HEXAGRAMS).filter(hex =>
    hex.journal_keywords.some(k => k.includes(lowerKeyword)) ||
    hex.shadow.toLowerCase().includes(lowerKeyword) ||
    hex.gift.toLowerCase().includes(lowerKeyword) ||
    hex.essence.toLowerCase().includes(lowerKeyword)
  );
}

// Export for use in journaling app
export default HEXAGRAMS;

